// ============================================================================
// Client coupons — single-use coupons a BM issues to this one client. The
// client can redeem it on the website/app or the BM can apply it on a PI; once
// an order is placed with it the backend switches it off (status "Used").
//
// Only the client's assigned BM, an admin or a store manager may create one;
// the backend decides and returns it as `can_create` alongside the list.
// ============================================================================
import { Button, Empty, Spin, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import {
  ClientCoupon,
  createClientCoupon,
  fetchClientCoupons,
} from "../../apis/client.api";
import CouponFormModal, {
  discountLabel,
  inr,
} from "../../components/misc/coupon_form_modal";

const STATUS_TAG: Record<ClientCoupon["status"], { color: string; label: string }> = {
  active: { color: "green", label: "Active" },
  used: { color: "blue", label: "Used" },
  expired: { color: "default", label: "Expired" },
  inactive: { color: "default", label: "Inactive" },
};

interface Props {
  clientId?: string;
}

const ClientCouponsSection: React.FC<Props> = ({ clientId }) => {
  const [rows, setRows] = useState<ClientCoupon[]>([]);
  const [canCreate, setCanCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    const res = await fetchClientCoupons(clientId);
    setRows(res.data);
    setCanCreate(res.can_create);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const columns: ColumnsType<ClientCoupon> = [
    {
      title: "Code",
      dataIndex: "coupon_code",
      render: (code: string) => <b style={{ fontFamily: "monospace" }}>{code}</b>,
    },
    {
      title: "Discount",
      render: (_, c) => (
        <>
          {discountLabel(c)}
          {c.is_free_delivery && <Tag color="blue" style={{ marginLeft: 8 }}>Free delivery</Tag>}
        </>
      ),
    },
    {
      title: "Min order",
      dataIndex: "min_cart_value",
      render: (v: number) => (v > 0 ? inr.format(v) : "—"),
    },
    {
      title: "3-month purchases above",
      dataIndex: "cumulative_sum_above",
      render: (v: number) => (v > 0 ? inr.format(v) : "—"),
    },
    {
      title: "Valid till",
      dataIndex: "valid_till",
      render: (d: string) => dayjs(d).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s: ClientCoupon["status"]) => (
        <Tag color={STATUS_TAG[s]?.color}>{STATUS_TAG[s]?.label ?? s}</Tag>
      ),
    },
    { title: "Note", dataIndex: "description", render: (d: string | null) => d || "—" },
    {
      title: "Created",
      dataIndex: "created_at",
      render: (d: string) => dayjs(d).format("DD MMM YYYY"),
    },
  ];

  return (
    <div>
      <div className="pb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="align-middle th-font-size-18 th-font-weight-600 primary-color-text">
          Coupons ({rows.length})
        </div>
        {canCreate && (
          <Button size="small" onClick={() => setOpen(true)} disabled={!clientId}>
            Create coupon
          </Button>
        )}
      </div>

      <CouponFormModal
        open={open}
        title="Create coupon for this client"
        intro="Only this client can use it, and only once. It switches off as soon as an order is placed with it."
        descriptionPlaceholder="Shown to the client with the coupon"
        onClose={() => setOpen(false)}
        onSubmit={(body) => createClientCoupon({ ...body, user_id: clientId as string })}
        onCreated={load}
      />

      {loading ? (
        <Spin style={{ display: "block", padding: 24 }} />
      ) : rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No coupons issued to this client" />
      ) : (
        <Table
          rowKey="user_coupon_id"
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={false}
        />
      )}
    </div>
  );
};

export default ClientCouponsSection;
